import { useEffect, useState } from "react";
import { Zap, Map, Radar, Quote } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal, MaskedLine } from "../components/Reveal";
import { NewsletterForm } from "../components/NewsletterForm";
import { StatCounter } from "../components/StatCounter";
import { api } from "../lib/api";

const contents = [
  { icon: Zap, title: "Tips actionnables", text: "Des stratégies concrètes de positionnement, pricing ou closing forfait — applicables dès lundi matin." },
  { icon: Map, title: "Plans d'action", text: "Des déroulés pas à pas pour construire chaque brique de ton système : offre, acquisition, preuve." },
  { icon: Radar, title: "Veille business", text: "Ce qui bouge dans le business : outils, formats, news décryptées." },
];

const sampleIssue = {
  subject: "Pourquoi ton TJM à 600€ te fait perdre de l'argent",
  excerpt: "La semaine dernière, un dev backend me disait : « Je suis à 600€/j, je ne peux pas monter. » Faux. Il ne peut pas monter en régie. Voici le calcul que personne ne t'a montré…",
  points: ["Le plafond invisible du modèle régie (avec les chiffres)", "Le test des 3 appels pour valider ton offre forfait", "Marché : les postes tech qui basculent au forfait en ce moment"],
};

export default function Newsletter() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  const subs = content?.stats?.find((s) => s.id === "st3");

  return (
    <div data-testid="newsletter-page" className="bg-[#0A0C10]">
      <Navbar />

      <section className="relative pt-40 pb-20 overflow-hidden grain">
        <div className="absolute top-[-30%] left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-primary/[0.07] blur-[120px] pointer-events-none" />
        <div className="container-site relative text-center">
          <p className="eyebrow mb-6">La porte d'entrée gratuite</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-4xl mx-auto">
            <MaskedLine delay={0.1}>Chaque semaine, de quoi</MaskedLine>
            <MaskedLine delay={0.25} className="text-primary text-glow">faire basculer ton activité.</MaskedLine>
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-6 text-slate-400 max-w-2xl mx-auto text-base sm:text-lg">
              Tips, plans d'action et infos concrètes pour sortir de la vente de ton temps.
              Lu en 5 minutes, appliqué en une semaine.
            </p>
          </Reveal>
          <Reveal delay={0.55} className="mt-10 max-w-xl mx-auto">
            <NewsletterForm testidPrefix="newsletter-page" />
            {subs && (
              <p className="mt-4 text-xs font-mono text-slate-500" data-testid="newsletter-subscriber-count">
                Déjà{" "}
                <span className="text-primary font-semibold">
                  <StatCounter value={subs.value} suffix={subs.suffix} decimals={subs.decimals} testid="newsletter-subs-stat" />
                </span>{" "}
                freelances abonnés
              </p>
            )}
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#0D1017] border-y border-[#262B3D]">
        <div className="container-site">
          <Reveal>
            <p className="eyebrow mb-4">Concret, chaque semaine</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Ce que tu reçois</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {contents.map((c, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div data-testid={`newsletter-content-${i}`} className="glass-card p-8 h-full transition-all duration-500 hover:border-primary/50 hover:-translate-y-1">
                  <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/30 flex items-center justify-center mb-5">
                    <c.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-bold mb-2">{c.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="container-site grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="eyebrow mb-4">Preuve de contenu</p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-[1.15]">
              Pas de remplissage. <span className="text-primary">Un vrai numéro, chaque semaine.</span>
            </h2>
            <p className="mt-4 text-slate-400 text-base leading-relaxed">
              Chaque édition part d'une situation réelle rencontrée dans l'accompagnement de freelances.
              Voici à quoi ressemble un numéro type.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div data-testid="newsletter-sample-issue" className="glass-card p-8 sm:p-10 relative overflow-hidden grain">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/0 via-primary to-primary/0" />
              <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-3">Numéro #47 · Extrait</p>
              <h3 className="font-heading text-xl sm:text-2xl font-bold mb-4 leading-snug">{sampleIssue.subject}</h3>
              <p className="text-sm text-slate-400 leading-relaxed mb-6">{sampleIssue.excerpt}</p>
              <ul className="space-y-3">
                {sampleIssue.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {content && content.testimonials.length > 0 && (
        <section className="py-16 sm:py-24 bg-[#0D1017] border-y border-[#262B3D]">
          <div className="container-site">
            <Reveal>
              <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mb-12">Ce qu'en disent les lecteurs</h2>
            </Reveal>
            <div className="grid md:grid-cols-3 gap-5">
              {content.testimonials.map((t, i) => (
                <Reveal key={t.id} delay={i * 0.08}>
                  <figure data-testid={`reader-quote-${i}`} className="glass-card p-7 h-full flex flex-col">
                    <Quote className="w-5 h-5 text-primary mb-4" />
                    <blockquote className="text-sm text-slate-400 leading-relaxed flex-1">« {t.quote} »</blockquote>
                    <div className="mt-5 pt-4 border-t border-[#262B3D]">
                      <p className="font-semibold text-sm">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.role}</p>
                    </div>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 sm:py-28 relative overflow-hidden">
        <div className="absolute bottom-[-40%] left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-primary/[0.07] blur-[120px] pointer-events-none" />
        <div className="container-site relative text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mb-8">
              Ta première bascule commence <span className="text-primary">dans ta boîte mail.</span>
            </h2>
            <div className="max-w-xl mx-auto">
              <NewsletterForm testidPrefix="newsletter-bottom" />
              <p className="mt-4 text-xs text-slate-500">Gratuit. Désinscription en 1 clic. Zéro spam.</p>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
